/** Inyectado en build desde backend/config/leadFlowTiming.js (vite.config.js) */
const LEAD_FLOW_POLICY_TIMING = typeof __LEAD_FLOW_TIMING__ !== 'undefined'
  ? __LEAD_FLOW_TIMING__
  : {
      defaultTaskDueMinutes: 240,
      visitFeedbackAfterEndMinutes: 0,
      qualificationDeferMinutes: 240,
      whatsappWaitExtendMinutes: 240,
    };

/** Reexportado desde config/leadFlowPolicy.js — no duplicar el valor aquí */
export const QUALIFICATION_DEFER_MINUTES = LEAD_FLOW_POLICY_TIMING.qualificationDeferMinutes;
export const LEAD_FLOW_TIMING = LEAD_FLOW_POLICY_TIMING;

/** Texto legible del plazo (p. ej. «4 horas» o «240 minutos»). */
export function formatLeadFlowPlazo(minutes) {
  const n = Number(minutes) || 0;
  if (n >= 60 && n % 60 === 0) {
    const h = n / 60;
    return h === 1 ? "1 hora" : `${h} horas`;
  }
  return `${n} minutos`;
}

export const QUALIFICATION_DEFER_LABEL = formatLeadFlowPlazo(QUALIFICATION_DEFER_MINUTES);

export const CONTACT_ORIGIN_LABELS = {
  call_first: "El lead ha contestado al primer intento de llamada.",
  call_second: "El lead ha contestado al segundo intento de llamada.",
  whatsapp: "El lead ha respondido por WhatsApp.",
  callback: "El cliente ha devuelto la llamada.",
  contact: "Hay contacto con el lead; indica el resultado de la cualificación.",
};

import { TASK_UI } from "./taskUiColors";

// A partir de la T3 (lead ya cualificado → existe ficha de cliente) las tarjetas del
// Pipeline y de Tareas abren la FICHA DEL CLIENTE en vez del modal del lead. Las etapas
// previas (T1*, T2*) siguen abriendo el modal del lead, incluso si el contacto ya tuviera
// ficha (un cliente existente que genera un lead nuevo entra por T1/T2 → modal del lead).
export const POST_QUALIFICATION_TASK_CODES = new Set([
  "T3", "T3B", "T3B1", "T3BN", "TC", "T4", "T4BN", "T4B1", "T4B2", "T4B3", "T7",
]);

/** true si el código de tarea corresponde a una etapa post-cualificación (T3 en adelante). */
export function isPostQualificationTaskCode(code) {
  return POST_QUALIFICATION_TASK_CODES.has(String(code || "").trim().toUpperCase());
}

export const T2A_QUALIFICATION_OUTCOMES = [
  {
    codigo: "cita_creada",
    label: "Cita creada",
    resultado: "Cita de visita creada tras la conversación",
    description: "Se crea el cliente y demanda. Has concertado cita y tienes que crearla en la aplicación",
    descriptionClienteExistente: "Has concertado cita y tienes que crearla en la aplicación.",
    accent: TASK_UI.cita,
    textColor: "#ffffff",
  },
  {
    codigo: "enviar_propiedad",
    label: "Mandar whatsapp",
    resultado: "Enviar propiedades acordadas por WhatsApp",
    description: "Sin cita, se crea el cliente y demanda. Tu tarea será enviar otra propiedad por WhatsApp",
    descriptionClienteExistente:
      "El cliente descarta la propiedad actual. Se quitará de sus resultados. Siguiente tarea: T4b3 · Enviar propiedad.",
    accent: TASK_UI.whatsapp,
    textColor: TASK_UI.textOnBright,
  },
  {
    codigo: "volver_a_llamar",
    label: "Volver a llamar",
    resultado: "Seguir por teléfono para intentar concertar visita",
    description: "Sin cita, se crea el cliente y demanda. Tu tarea será volverlo a llamar.",
    descriptionClienteExistente: "Tu tarea será volver a llamar al cliente.",
    accent: TASK_UI.llamada,
    textColor: "#000000",
  },
  {
    codigo: "frios",
    label: "Fríos",
    resultado: "Lead sin interés activo — pasa a Fríos",
    description: "El lead y el cliente pasan a Fríos.",
    accent: TASK_UI.sinInteres,
    textColor: TASK_UI.textOnBright,
    secondary: true,
  },
  {
    codigo: "eliminados",
    label: "Eliminados",
    resultado: "Lead sin interés — pasa a Eliminados",
    description: "El lead pasa a Eliminados.",
    accent: TASK_UI.espera,
    textColor: "#ffffff",
    secondary: true,
  },
];

export function parseTaskMetadata(task = {}) {
  if (!task?.metadata) return {};
  if (typeof task.metadata === "object") return task.metadata;
  try {
    return JSON.parse(task.metadata);
  } catch {
    return {};
  }
}

export function isLeadTaskExpired(tarea = {}) {
  if (!tarea?.fecha_vencimiento) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  // TC · Cita: el due es el fin de la visita → transición a T4, no «vencida» en rojo.
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() === "TC" && meta.agenda_id) return false;
  const due = new Date(tarea.fecha_vencimiento);
  return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
}

export function isTaskModalSnoozed(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  return meta.task_modal_snoozed === true || meta.task_modal_snoozed === "true";
}

/** Tarea del flujo lead asignada a Ana (voz / WhatsApp). */
export function isAnaManagedLeadTask(tarea = {}) {
  // null explícito no dispara el default `= {}` → no leer .asignada_a a ciegas.
  if (!tarea || typeof tarea !== "object") return false;
  const meta = parseTaskMetadata(tarea);
  const owner = String(meta.task_owner || tarea.asignada_a || "").trim().toLowerCase();
  if (owner === "ana") return true;
  return String(meta.task_owner_label || "").trim().toLowerCase() === "ana";
}

/** T2A del comercial (no Ana): M1 en CRM aunque arrastre flags Ana colgados. */
export function isCommercialT2ATask(tarea = {}) {
  if (!tarea?.id) return false;
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T2A") return false;
  const owner = String(meta.task_owner || tarea.asignada_a || "")
    .trim()
    .toLowerCase();
  return owner === "agente";
}

/**
 * T2A cualificada por Ana por WhatsApp (M1 interno).
 * El agente no debe ver ModalResultadoCualificacion en modo T2A.
 * Invariante: si task_owner/asignada_a es agente, NUNCA es cualificación Ana
 * (regresión Test1/Test2/Antonia: ana_handles_qualification colgado → M1 no abre).
 */
export function isAnaQualificationTask(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T2A") return false;
  if (isCommercialT2ATask(tarea)) return false;
  return (
    isAnaManagedLeadTask(tarea) ||
    meta.ana_handles_qualification === true ||
    meta.ana_handles_qualification === "true"
  );
}

export function taskNeedsQualificationModal(tarea = {}) {
  if (!tarea?.id) return false;
  if (isAnaQualificationTask(tarea)) return false;
  if (isLeadTaskExpired(tarea)) return false;
  if (isTaskModalSnoozed(tarea)) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T2A") return false;
  // Comercial: abrir M1 aunque falte requires_qualification_outcome (flags Ana colgados).
  if (isCommercialT2ATask(tarea)) return true;
  const needsOutcome = meta.requires_qualification_outcome;
  return needsOutcome === true || needsOutcome === "true";
}

/**
 * Como taskNeedsQualificationModal pero sin comprobar el flag snoozed.
 * Úsala en aperturas manuales (clic en tarea) y en condiciones de render de modales manuales.
 */
export function taskIsQualificationType(tarea = {}) {
  if (!tarea?.id) return false;
  if (isAnaQualificationTask(tarea)) return false;
  if (isLeadTaskExpired(tarea)) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T2A") return false;
  if (isCommercialT2ATask(tarea)) return true;
  const needsOutcome = meta.requires_qualification_outcome;
  return needsOutcome === true || needsOutcome === "true";
}

/**
 * Como taskNeedsVisitConfirmModal pero sin comprobar el flag snoozed.
 * Úsala en aperturas manuales (clic en tarea) y en condiciones de render de modales manuales.
 */
export function taskIsVisitConfirmType(tarea = {}) {
  if (!tarea?.id) return false;
  if (isLeadTaskExpired(tarea)) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  if (isT3bVisitConfirmWait(tarea)) return true;
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").toUpperCase();
  const needsOutcome = meta.requires_visit_confirm_outcome;
  if (needsOutcome === false || needsOutcome === "false") return false;
  if (code === "T3AE" && (needsOutcome === true || needsOutcome === "true")) return true;
  if (code !== "T3") return false;
  return true;
}

export function hasWhatsappReplyPending(tarea = {}) {
  if (!tarea?.id) return false;
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").toUpperCase();
  // T4 · Esperar respuesta (post visita): misma familia T4, reply → Evaluar / M2.
  if (!["T3B", "T3B1", "T2E", "T4", "T4B1", "T4B2", "T4B3"].includes(code)) return false;
  if (code === "T4" && !isT4PostVisitaEspera(tarea)) return false;
  return (
    meta.whatsapp_reply_pending === true ||
    meta.whatsapp_reply_pending === "true" ||
    Boolean(meta.whatsapp_inbound_received_at)
  );
}

/** T4b1 en espera de respuesta tras llamada o WhatsApp — confirmar abre M1. */
export function isT4b1QualificationWait(tarea = {}) {
  if (!tarea?.id) return false;
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T4B1") return false;
  return isWhatsappWaitingTask(tarea);
}

/** Tarea T4b1 (cualquier subestado). */
export function isT4b1Task(tarea = {}) {
  if (!tarea?.id) return false;
  return String(parseTaskMetadata(tarea).task_code || "").toUpperCase() === "T4B1";
}

/**
 * T4b1 pendiente de contacto (llamar / WhatsApp) o en espera sin respuesta.
 * No implica abrir M2: el feedback de visita solo cuando taskIsM2Type (hay respuesta).
 */
export function isT4b1AutoContactTask(tarea = {}) {
  if (!isT4b1Task(tarea)) return false;
  if (taskIsM2Type(tarea)) return false;
  // Respuesta por WA → M1 (isT4b1WhatsappQualificationReply), no «auto-contact».
  if (isT4b1WhatsappQualificationReply(tarea)) return false;
  return true;
}

/** T4b1 insistencia / primer contacto: aún no hay llamada ni WA registrados. */
export function isT4b1PendingContact(tarea = {}) {
  if (!isT4b1Task(tarea)) return false;
  if (taskIsM2Type(tarea)) return false;
  if (isT4b1WhatsappQualificationReply(tarea)) return false;
  if (isWhatsappWaitingTask(tarea)) return false;
  return true;
}

/** T4b1 tras WA/llamada post-visita: la respuesta se registra con M2. */
export function isT4bVisitResultWait(tarea = {}) {
  if (!tarea?.id) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
  if (code !== "T4B1") return false;
  return isWhatsappWaitingTask(tarea);
}

/** T4b2/T4b3 (objeciones/descarte tras visita): tras enviar WA y recibir respuesta → M1. */
export function isT4bQualificationWait(tarea = {}) {
  if (!tarea?.id) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
  if (!["T4B2", "T4B3"].includes(code)) return false;
  return isWhatsappWaitingTask(tarea) && hasWhatsappReplyPending(tarea);
}

export function isT4bObjecionesDescarteTask(tarea = {}) {
  const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
  return ["T4B2", "T4B3"].includes(code);
}

/** T3b/T3b1/T2e (concertar visita) con respuesta del cliente pendiente de registrar en el M1. */
export function isT3bVisitConfirmWait(tarea = {}) {
  // Ana gestiona el WhatsApp: el agente no debe ver el modal de cualificación.
  if (isAnaManagedLeadTask(tarea)) return false;
  const meta = parseTaskMetadata(tarea);
  if (
    meta.ana_handles_whatsapp === true ||
    meta.ana_handles_whatsapp === "true" ||
    meta.ana_handles_qualification === true ||
    meta.ana_handles_qualification === "true"
  ) {
    return false;
  }
  const code = String(meta.task_code || "").toUpperCase();
  if (!["T3B", "T3B1", "T2E"].includes(code)) return false;
  return isWhatsappWaitingTask(tarea) && hasWhatsappReplyPending(tarea);
}

/** T3 tras respuesta WhatsApp: agendar visita o siguiente paso. */
export function taskNeedsVisitConfirmModal(tarea = {}) {
  if (!tarea?.id) return false;
  if (isLeadTaskExpired(tarea)) return false;
  if (isTaskModalSnoozed(tarea)) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;

  if (isT3bVisitConfirmWait(tarea)) return true;

  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").toUpperCase();
  const needsOutcome = meta.requires_visit_confirm_outcome;
  if (needsOutcome === false || needsOutcome === "false") return false;
  if (code === "T3AE" && (needsOutcome === true || needsOutcome === "true")) return true;
  if (code !== "T3") return false;
  return true;
}

/** Modo del modal M1 según tarea activa. */
export function getM1ModalMode(tarea = {}) {
  if (taskIsVisitConfirmType(tarea)) return "t3";
  return "t2a";
}

/** Tareas que abren M1 (Modal Resultado Cualificación). */
export function taskIsM1Type(tarea = {}) {
  return (
    taskIsQualificationType(tarea) ||
    taskIsVisitConfirmType(tarea) ||
    isT4bQualificationWait(tarea) ||
    isT4b1WhatsappQualificationReply(tarea)
  );
}

export function taskNeedsM1Modal(tarea = {}) {
  if (taskNeedsQualificationModal(tarea) || taskNeedsVisitConfirmModal(tarea)) return true;
  if (isT4b1WhatsappQualificationReply(tarea) && !isTaskModalSnoozed(tarea)) return true;
  // T4b2/T4b3 (objeciones/descarte) auto-abren M1 cuando llega la respuesta del cliente.
  if (
    isT4bQualificationWait(tarea) &&
    hasWhatsappReplyPending(tarea) &&
    !isTaskModalSnoozed(tarea)
  ) {
    return true;
  }
  return false;
}

/** True si la cita vinculada a la tarea ya terminó (fin de agenda). */
export function visitAppointmentHasEndedForTask(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const end = meta.agenda_end_at || meta.scheduled_end_at;
  if (!end) return false;
  const endMs = new Date(end).getTime();
  if (Number.isNaN(endMs)) return false;
  return Date.now() >= endMs;
}

/** T4b1 tras envío WA: la respuesta del cliente se cualifica con M1 (no M2). */
export function isT4b1WhatsappQualificationReply(tarea = {}) {
  if (!tarea?.id) return false;
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T4B1") return false;
  if (!hasWhatsappReplyPending(tarea)) return false;
  return Boolean(meta.t4b1_whatsapp_sent_at);
}

/** Opciones M1 (T2A) adaptadas cuando el cliente/demanda ya existen. */
export function getM1QualificationOutcomes(lead = {}, tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const taskCode = String(meta.task_code || "").toUpperCase();
  const clienteExiste =
    Boolean(lead?.cliente_id) || isPostQualificationTaskCode(taskCode);

  return T2A_QUALIFICATION_OUTCOMES.map((op) => {
    if (op.secondary || !clienteExiste) return { ...op };
    const alt = op.descriptionClienteExistente;
    if (!alt) return { ...op };
    return { ...op, description: alt };
  });
}

/** T4 · Esperar respuesta (post «sigue el proceso»): misma familia T4, sin inventar T4E. */
export function isT4PostVisitaEspera(tarea = {}) {
  if (!tarea?.id) return false;
  const meta = parseTaskMetadata(tarea);
  if (String(meta.task_code || "").toUpperCase() !== "T4") return false;
  return (
    meta.t4_post_visita_espera === true ||
    meta.t4_post_visita_espera === "true" ||
    Boolean(meta.t4_espera_started_at) ||
    (meta.flow_substate === "esperar_respuesta" &&
      (meta.t4_waiting_response === true || meta.t4_waiting_response === "true"))
  );
}

/** @deprecated alias — usar isT4PostVisitaEspera */
export function isT4eVisitFeedbackWait(tarea = {}) {
  return isT4PostVisitaEspera(tarea);
}

/** T4 Cualificar (visita ya terminada) o T4 espera/T4b1 con respuesta — M2, no M1 WA. */
export function isVisitReadyForFeedbackM2(tarea = {}) {
  if (!tarea?.id) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  if (isT4b1WhatsappQualificationReply(tarea)) return false;
  if (isT4bVisitResultWait(tarea) && hasWhatsappReplyPending(tarea)) return true;
  // T4 · Esperar respuesta: solo M2 cuando el cliente ya contestó.
  if (isT4PostVisitaEspera(tarea)) {
    return hasWhatsappReplyPending(tarea);
  }
  // TC = cita en curso → editar en agenda; M2 solo tras vencer TC y pasar a T4 Cualificar.
  if (isT4FeedbackTask(tarea)) {
    return visitAppointmentHasEndedForTask(tarea);
  }
  return false;
}

/** Tareas que abren M2 (Modal Resultado Visita) — solo cuando toca cualificar la visita. */
export function taskIsM2Type(tarea = {}) {
  return isVisitReadyForFeedbackM2(tarea);
}

export function taskNeedsM2Modal(tarea = {}) {
  if (isTaskModalSnoozed(tarea)) return false;
  return isVisitReadyForFeedbackM2(tarea);
}

/** TC pendiente con cita en agenda: abrir feedback solo al clicar (no automático). */
export function isVisitFeedbackTask(tarea = {}) {
  if (!tarea?.id) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const meta = parseTaskMetadata(tarea);
  return String(meta.task_code || "").toUpperCase() === "TC" && Boolean(meta.agenda_id);
}

/** T4 pendiente: feedback interesado/no_interesado tras visita realizada. */
export function isT4FeedbackTask(tarea = {}) {
  if (!tarea?.id) return false;
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const meta = parseTaskMetadata(tarea);
  return String(meta.task_code || "").toUpperCase() === "T4";
}

/** @deprecated Usar isVisitFeedbackTask — sin apertura automática por vencimiento. */
export function taskNeedsVisitFeedbackModal(tarea = {}) {
  return isVisitFeedbackTask(tarea);
}

export function getVisitAgendaId(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  return meta.agenda_id ? String(meta.agenda_id) : null;
}

/** T1c, T2e legacy, T3b o T3b1: WhatsApp enviado y en espera de respuesta del lead. */
export function isWhatsappWaitingTask(tarea = {}) {
  if (!tarea?.id) return false;
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").toUpperCase();
  const titulo = String(tarea?.titulo || "");

  if (meta.flow_substate === "esperar_respuesta") return true;
  if (/esperar respuesta/i.test(titulo)) return true;

  if (code === "T1C" && (meta.t1c_whatsapp_sent_at || meta.t1c_email_sent_at)) return true;
  if ((code === "T2E" || code === "T3B") && (meta.t2e_whatsapp_sent_at || meta.t3b_whatsapp_sent_at)) {
    return true;
  }
  if (code === "T3B1" && meta.t3b1_whatsapp_sent_at) return true;
  // T4 · Esperar respuesta (muy interesado…); oculta en tablero Tareas.
  if (code === "T4" && isT4PostVisitaEspera(tarea)) return true;
  if (code === "T4B1" && (meta.t4b1_whatsapp_sent_at || meta.t4b1_call_at)) return true;
  if (code === "T4B2" && meta.t4b2_whatsapp_sent_at) return true;
  if (code === "T4B3" && meta.t4b3_whatsapp_sent_at) return true;
  return false;
}

/** T3bN / T4bN: última oportunidad de buscar propiedad antes de Fríos. */
export function isSinPropiedadesBuscarTask(tarea = {}) {
  const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
  return code === "T3BN" || code === "T4BN";
}

/** Tareas de envío WhatsApp del flujo lead (pendiente de envío o ya en espera). */
export function isWhatsappFlowTask(tarea = {}) {
  if (!tarea?.id) return false;
  const code = String(parseTaskMetadata(tarea).task_code || "").toUpperCase();
  return ["T1C", "T2E", "T3B", "T3B1", "T3BN", "T4BN", "T4B1", "T4B2", "T4B3"].includes(code);
}

export function getQualificationOriginLabel(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  return CONTACT_ORIGIN_LABELS[meta.contact_origin] || CONTACT_ORIGIN_LABELS.contact;
}

/** Opciones rápidas de resultado por código de tarea del flujo lead entrante. */
export const FLOW_OUTCOMES = {
  T2A: T2A_QUALIFICATION_OUTCOMES.map(({ codigo, label, resultado, secondary }) => ({
    codigo,
    label,
    resultado,
    secondary,
  })),
  T2B: [
    { label: "Acepta cita", codigo: "acepta_cita", resultado: "Acepta concertar la cita" },
    { label: "No acepta", codigo: "no_acepta", resultado: "No acepta la cita propuesta" },
  ],
  T1C: [
    { label: "Responde", codigo: "responde", resultado: "El lead respondió al WhatsApp de Ana" },
    { label: "No responde", codigo: "no_responde", resultado: "El lead no respondió al WhatsApp de Ana" },
  ],
  T3B: [
    { label: "Responde", codigo: "responde", resultado: "El lead respondió al WhatsApp de concertación" },
    { label: "No responde", codigo: "no_responde", resultado: "Sin respuesta al WhatsApp de concertación" },
  ],
  T3B1: [
    { label: "Responde", codigo: "responde", resultado: "El lead respondió al segundo WhatsApp de concertación" },
    { label: "No responde", codigo: "no_responde", resultado: "Sin respuesta al segundo WhatsApp de concertación" },
  ],
  // T4 / T4b1 no usan quick outcomes — M2 (ModalResultadoVisita) tras contacto detectado
  T4: [],
  T4B1: [],
  T4B2: [
    { label: "WhatsApp enviado", codigo: "enviado", resultado: "WhatsApp enviado al cliente para resolver objeciones" },
  ],
  T4B3: [
    { label: "WhatsApp enviado", codigo: "enviado", resultado: "WhatsApp enviado al cliente tras descarte" },
  ],
  T7: [
    { label: "Reservado", codigo: "reserva_confirmada", resultado: "Reserva confirmada con el cliente" },
    { label: "Operación cerrada", codigo: "operacion_cerrada", resultado: "Operación de compraventa cerrada" },
  ],
  T3BN: [
    { label: "Envié propiedad por WA", codigo: "enviado", resultado: "Propiedad enviada por WhatsApp" },
    { label: "Sin propiedad → Fríos", codigo: "no_enviado", resultado: "No hay propiedad nueva para enviar" },
  ],
  T4BN: [
    { label: "Envié propiedad por WA", codigo: "enviado", resultado: "Propiedad enviada por WhatsApp" },
    { label: "Sin propiedad → Fríos", codigo: "no_enviado", resultado: "No hay propiedad nueva para enviar" },
  ],
};

/** Opciones del modal T3 tras respuesta WhatsApp (con siguiente tarea explícita). */
export const T3_VISIT_CONFIRM_OUTCOMES = [
  {
    codigo: "cita_creada",
    label: "Concertar cita",
    resultado: "Cita de visita creada en agenda",
    description: "Abre el calendario, crea la visita y pasa el lead a Visitantes con tarea Cita (TC).",
    accent: TASK_UI.cita,
    textColor: "#ffffff",
  },
  {
    codigo: "enviar_whatsapp",
    label: "Enviar WhatsApp de concertación",
    resultado: "Enviar WhatsApp al lead para concertar la visita",
    description: "Siguiente tarea: T3b · WhatsApp al lead (5 min para enviar y esperar respuesta).",
    accent: TASK_UI.whatsapp,
    textColor: TASK_UI.textOnBright,
  },
  {
    codigo: "volver_a_llamar",
    label: "Volver a llamar",
    resultado: "Seguir por teléfono para concertar visita",
    description: "Siguiente tarea: Volver a llamar (5 min).",
    accent: TASK_UI.llamada,
    textColor: "#000000",
  },
  {
    codigo: "frios",
    label: "Fríos",
    resultado: "Lead sin interés activo — pasa a Fríos",
    description: "El lead y el cliente pasan a Fríos.",
    accent: TASK_UI.sinInteres,
    textColor: TASK_UI.textOnBright,
    secondary: true,
  },
  {
    codigo: "eliminados",
    label: "Eliminados",
    resultado: "Lead sin interés — pasa a Eliminados",
    description: "El lead pasa a Eliminados.",
    accent: TASK_UI.espera,
    textColor: "#ffffff",
    secondary: true,
  },
];
